import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/react';
import { useTranslation } from '@yorukiri/i18n';
import { Button } from '@yorukiri/ui';
import { colors } from '@yorukiri/ui/tokens';
import { Heatmap } from '../components/Heatmap';
import { PageTile } from '../components/PageTile';
import { Clock, Play, Plus, Sparkles } from '../components/icons';
import { useCreatePage, usePages, useReviewSummary } from '../lib/queries';

function greetingKey() {
  const h = new Date().getHours();
  if (h < 5) return 'greeting_late_night' as const;
  if (h < 12) return 'greeting_morning' as const;
  if (h < 18) return 'greeting_afternoon' as const;
  return 'greeting_evening' as const;
}

type Filter = 'all' | 'active' | 'archived';

export function LibraryRoute() {
  const [filter, setFilter] = useState<Filter>('all');
  const { t, i18n } = useTranslation('library');
  const { t: tr } = useTranslation('review');
  const { user } = useUser();
  const navigate = useNavigate();
  const { data: pages, isLoading } = usePages();
  const summaryQuery = useReviewSummary();
  const createPage = useCreatePage();

  const handleNewPage = async () => {
    const page = await createPage.mutateAsync({});
    navigate(`/pages/${page.id}`);
  };

  const firstName =
    user?.firstName ?? user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? '';

  const dateLabel = new Date()
    .toLocaleDateString(i18n.resolvedLanguage ?? undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    })
    .toUpperCase();

  const summary = summaryQuery.data;
  const totalReady =
    (summary?.totalDue ?? 0) + (summary?.totalNew ?? 0) + (summary?.totalLearning ?? 0);
  const hasAnyDue = (summary?.byPage ?? []).some(
    (p) => p.due + p.new + p.learning > 0,
  );

  // Count map keyed by each page's root ancestor so tiles for child pages also
  // surface the parent's rolled-up review counts.
  const countsByRootId = useMemo(() => {
    const m = new Map<string, { due: number; learning: number; new: number }>();
    for (const p of summary?.byPage ?? []) {
      m.set(p.pageId, { due: p.due, learning: p.learning, new: p.new });
    }
    return m;
  }, [summary]);

  // R keyboard shortcut: start review across all decks.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement && ['INPUT', 'TEXTAREA'].includes(e.target.tagName))
        return;
      if ((e.key === 'r' || e.key === 'R') && hasAnyDue) {
        e.preventDefault();
        navigate('/review/all');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasAnyDue, navigate]);

  const rootForPage = (pageId: string): string => {
    const byId = new Map((pages ?? []).map((p) => [p.id, p]));
    let cur = pageId;
    while (true) {
      const p = byId.get(cur);
      if (!p?.parentPageId || !byId.has(p.parentPageId)) return cur;
      cur = p.parentPageId;
    }
  };

  return (
    <div style={{ padding: '32px 40px 60px' }}>
      <header
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 40,
          alignItems: 'end',
          paddingBottom: 28,
          borderBottom: `1px solid ${colors.line}`,
          marginBottom: 28,
        }}
      >
        <div>
          <div
            style={{
              fontVariantNumeric: 'tabular-nums',
              fontSize: 11,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: colors.ink3,
              marginBottom: 12,
            }}
          >
            {dateLabel}
          </div>
          <h1
            style={{
              fontWeight: 600,
              letterSpacing: '-0.02em',
              fontSize: 54,
              lineHeight: 1,
              margin: 0,
              marginBottom: 12,
            }}
          >
            {t(greetingKey())},{' '}
            <em style={{ fontStyle: 'italic', color: colors.ink3 }}>{firstName}</em>
          </h1>
          <p style={{ fontSize: 14, color: colors.ink2, maxWidth: '56ch' }}>
            {t('subcopy', { count: totalReady, pages: pages?.length ?? 0 })}
          </p>
        </div>
        <Link to={hasAnyDue ? '/review/all' : '#'} style={{ textDecoration: 'none' }}>
          <Button size="lg" style={{ gap: 10 }} disabled={!hasAnyDue}>
            <Play size={13} />
            {t('start_review')}
            {totalReady > 0 && (
              <span
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 12,
                  background: 'rgba(255,255,255,0.12)',
                  padding: '2px 7px',
                  borderRadius: 4,
                }}
              >
                {totalReady}
              </span>
            )}
            <kbd
              style={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: 10,
                opacity: 0.6,
                border: '1px solid rgba(255,255,255,0.2)',
                padding: '1px 5px',
                borderRadius: 3,
              }}
            >
              {t('kbd_start_review')}
            </kbd>
          </Button>
        </Link>
      </header>

      <StatGrid
        due={summary?.totalDue ?? 0}
        learning={summary?.totalLearning ?? 0}
        newCount={summary?.totalNew ?? 0}
        studied={summary?.studied30d ?? 0}
      />

      <SectionHead filter={filter} setFilter={setFilter} />

      {isLoading ? (
        <div style={{ color: colors.ink3, marginBottom: 44 }}>…</div>
      ) : (pages?.length ?? 0) === 0 ? (
        <EmptyState onCreate={handleNewPage} busy={createPage.isPending} />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 16,
            marginBottom: 44,
          }}
        >
          {pages?.map((p) => (
            <PageTile
              key={p.id}
              page={p}
              counts={countsByRootId.get(rootForPage(p.id))}
            />
          ))}
          <NewPageTile onCreate={handleNewPage} busy={createPage.isPending} />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
        <div>
          <SubHead
            title={tr('home_recent')}
            hint={
              summary && summary.streak > 0
                ? tr('home_streak', { days: summary.streak })
                : undefined
            }
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(summary?.recentSessions ?? []).length === 0 ? (
              <div style={{ fontSize: 12, color: colors.ink3, padding: '10px 0' }}>
                {tr('home_no_sessions')}
              </div>
            ) : (
              (summary?.recentSessions ?? []).map((s, i) => (
                <RecentSessionRow key={`${s.pageId}-${s.day}-${i}`} session={s} />
              ))
            )}
          </div>
        </div>
        <div>
          <SubHead title={tr('home_streak', { days: summary?.streak ?? 0 })} />
          <Heatmap />
        </div>
      </div>
    </div>
  );
}

function StatGrid({
  due,
  learning,
  newCount,
  studied,
}: {
  due: number;
  learning: number;
  newCount: number;
  studied: number;
}) {
  const { t } = useTranslation('review');
  const items = [
    { key: 'due', value: due, label: t('home_stat_due'), tone: colors.accent },
    { key: 'learning', value: learning, label: t('home_stat_learning'), tone: colors.warn },
    { key: 'new', value: newCount, label: t('home_stat_new'), tone: colors.ink2 },
    { key: 'studied', value: studied, label: t('home_stat_studied'), tone: colors.good },
  ];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 12,
        marginBottom: 40,
      }}
    >
      {items.map((item) => (
        <div
          key={item.key}
          style={{
            background: colors.surface2,
            border: `1px solid ${colors.line}`,
            borderRadius: 9,
            padding: '12px 14px 14px',
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: colors.ink3,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {item.label}
          </div>
          <div
            style={{
              fontSize: 30,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              lineHeight: 1.05,
              color: item.tone,
              marginTop: 6,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {item.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function RecentSessionRow({
  session,
}: {
  session: {
    day: string;
    pageId: string;
    pageTitle: string;
    reviewed: number;
    again: number;
  };
}) {
  const { t, i18n } = useTranslation('review');
  const when = new Date(`${session.day}T00:00:00Z`).toLocaleDateString(
    i18n.resolvedLanguage ?? undefined,
    { month: 'short', day: 'numeric' },
  );
  return (
    <Link
      to={`/review/${session.pageId}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        borderBottom: `1px solid ${colors.line}`,
        textDecoration: 'none',
        color: colors.ink,
      }}
    >
      <Clock size={11} style={{ color: colors.ink3, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {session.pageTitle}
        </div>
        <div style={{ fontSize: 11, color: colors.ink3, marginTop: 1 }}>{when}</div>
      </div>
      <div style={{ fontSize: 11, color: colors.ink3, fontVariantNumeric: 'tabular-nums' }}>
        {t('home_recent_summary', { reviewed: session.reviewed, again: session.again })}
      </div>
    </Link>
  );
}

function SubHead({ title, hint }: { title: string; hint?: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        marginBottom: 12,
      }}
    >
      <h3
        style={{
          fontWeight: 600,
          letterSpacing: '-0.015em',
          fontSize: 22,
          margin: 0,
        }}
      >
        {title}
      </h3>
      {hint && <span style={{ fontSize: 11.5, color: colors.ink3 }}>{hint}</span>}
    </div>
  );
}

function EmptyState({ onCreate, busy }: { onCreate: () => void; busy: boolean }) {
  return (
    <div
      style={{
        border: `1px dashed ${colors.line2}`,
        borderRadius: 10,
        padding: '56px 40px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        color: colors.ink3,
        marginBottom: 44,
      }}
    >
      <Sparkles size={28} />
      <div style={{ fontSize: 15, color: colors.ink2, fontStyle: 'italic' }}>
        No pages yet. Start with a blank one.
      </div>
      <Button onClick={onCreate} disabled={busy} style={{ gap: 6, marginTop: 8 }}>
        <Plus size={13} /> Create your first page
      </Button>
    </div>
  );
}

function NewPageTile({ onCreate, busy }: { onCreate: () => void; busy: boolean }) {
  return (
    <button
      onClick={onCreate}
      disabled={busy}
      style={{
        background: 'transparent',
        border: `1px dashed ${colors.line2}`,
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        color: colors.ink3,
        minHeight: 140,
        cursor: busy ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit',
        opacity: busy ? 0.5 : 1,
      }}
    >
      <Plus size={18} />
      <span style={{ fontSize: 14, fontStyle: 'italic' }}>New page</span>
    </button>
  );
}

function SectionHead({ filter, setFilter }: { filter: Filter; setFilter: (f: Filter) => void }) {
  const { t } = useTranslation('library');
  const filters: { id: Filter; key: 'all' | 'active' | 'archived' }[] = [
    { id: 'all', key: 'all' },
    { id: 'active', key: 'active' },
    { id: 'archived', key: 'archived' },
  ];
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 16,
      }}
    >
      <h3
        style={{
          fontWeight: 600,
          letterSpacing: '-0.015em',
          fontSize: 26,
          margin: 0,
        }}
      >
        {t('section_pages')}
      </h3>
      <div
        style={{
          display: 'flex',
          gap: 2,
          background: colors.surface2,
          padding: 2,
          borderRadius: 6,
          border: `1px solid ${colors.line2}`,
        }}
      >
        {filters.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            style={{
              background: filter === f.id ? colors.surface : 'transparent',
              border: 'none',
              padding: '5px 11px',
              borderRadius: 4,
              fontFamily: 'inherit',
              fontSize: 12,
              color: filter === f.id ? colors.ink : colors.ink2,
              cursor: 'pointer',
              boxShadow: filter === f.id ? '0 1px 2px rgba(24,22,15,0.08)' : 'none',
            }}
          >
            {t(`filters.${f.key}`)}
          </button>
        ))}
      </div>
    </div>
  );
}
